<template>
  <div>
    <page-header
      :class="isMobile && 'mt-3'"
      left-side-class="col-xl-5"
      :page="pageConfig"
      right-side-class="col-xl-7 align-items-end d-flex pb-3 justify-content-end"
    />
    <filter-manager
      ref="filter"
      v-model="filter.model"
      class="filter-manager_extended"
      :display-advanced="false"
      :filters="filterManagerConfig"
      search-container-class="search-btn"
      @academicYear="onAcademicYearChange"
      @disciplineType="onDisciplineTypeChange"
      @locationService="onLocationServiceChange"
      @provider="onProviderChange"
      @search="onSearchHandler"
    />
    <support-type-legend />
    <available-hours
      :is-mobile="isMobile"
      :options="sortedHours"
      @select="onSelectHours"
    />
    <visit-calendar
      :academic-year-id="
        filter.model.academicYear ? filter.model.academicYear.id : null
      "
      :dates="dates"
      :is-mobile="isMobile"
      :students="studentsFilter"
      @on-datarange-changed="onDatarangeChanged"
      @reload="search"
    />

    <div class="col-12" />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import { name as appModuleName } from '@/modules/app/store/index';
import { mapState, mapMutations, mapGetters } from 'vuex';
import AvailableHours from './components/AvailableHours';
import SupportTypeLegend from './components/SupportTypeLegend.vue';
import VisitCalendar from './components/VisitCalendar';
import FilterManager from 'FilterManager';
import { getAcademicYearsToType as _getAcademicYearsToType } from '@/modules/shared/academicyear.api';
import { getProvidersByAcademicyearId } from '@/modules/shared/provider.api';
import roles from '@/utils/constants/roles.js';
import moment from 'moment';
import {
  getList as _getServiceLocations,
  getSpecialistList as _getSpecialistList,
  getDisciplineList as _getDisciplineList,
} from '@/modules/shared/servicelocation.api';
import serviceTypes from '@/utils/constants/serviceTypes.js';
import {
  getDetailBySpecialistAndDates as _getDetailBySpecialistAndDates,
  getDetailBySpecialist as _getDetailBySpecialist,
} from '@/modules/shared/servicelocation.api';

export default {
  name: 'Main',
  components: {
    PageHeader,
    AvailableHours,
    SupportTypeLegend,
    VisitCalendar,
    FilterManager,
  },
  data: () => ({
    hourOptions: [
      {
        hourFrom: 7,
        from: '07:00',
        to: '8:00',
        hourTo: 9,
        label: '7:00 AM - 8:00 AM',
      },
      {
        hourFrom: 8,
        from: '08:00',
        to: '9:00',
        hourTo: 9,
        label: '8:00 AM - 9:00 AM',
      },
      {
        hourFrom: 9,
        from: '09:00',
        to: '10:00',
        hourTo: 10,
        label: '9:00 AM - 10:00 AM',
      },
      {
        hourFrom: 10,
        from: '10:00',
        to: '11:00',
        hourTo: 11,
        label: '10:00 AM - 11:00 AM',
      },
      {
        hourFrom: 11,
        from: '11:00',
        to: '12:00',
        hourTo: 12,
        label: '11:00 AM - 12:00 PM',
      },
      {
        hourFrom: 13,
        from: '13:00',
        to: '14:00',
        hourTo: 14,
        label: '1:00 PM - 2:00 PM',
      },
      {
        hourFrom: 14,
        from: '14:00',
        to: '15:00',
        hourTo: 15,
        label: '2:00 PM - 3:00 PM',
      },
      {
        hourFrom: 15,
        from: '15:00',
        to: '16:00',
        hourTo: 16,
        label: '3:00 PM - 4:00 PM',
      },
      {
        hourFrom: 16,
        from: '16:00',
        to: '17:00',
        hourTo: 17,
        label: '4:00 PM - 5:00 PM',
      },
    ],
    sortedHours: [],
    selectedHours: 'Todos',
    filter: {
      model: {
        academicYear: null,
        provider: null,
        locationService: null,
        disciplineType: null,
        specialist: null,
      },
      options: {
        academicYears: [],
        providers: [],
        locationServices: [],
        disciplineTypes: [],
        specialists: [],
      },
    },
    pagination: {
      results: [],
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: 15,
    },
    dates: null,
    availableStudents: [],
  }),
  computed: {
    ...mapState('$_user', ['user', 'companies']),
    ...mapGetters(appModuleName, {
      isMobile: 'getIsMobileDensity',
    }),
    pageConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Calendario de Registro de Visitas',
        routes: ['Proveedor', 'Calendario de Registro de Visitas'],
      };
    },
    studentsFilter() {
      if (this.availableStudents.length === 0) {
        return [];
      }

      if (this.selectedHours === 'Todos') {
        return this.hourOptions.map((hourOption) => {
          const fromHour = parseInt(hourOption.from.split(':')[0], 10);
          return {
            startDate: this.dates.startDate,
            endDate: this.dates.endDate,
            hour: hourOption,
            students: this.filterStudentsByHourRange(fromHour),
          };
        });
      } else {
        const fromHour = parseInt(this.selectedHours.from.split(':')[0], 10);
        return [
          {
            startDate: this.dates.startDate,
            endDate: this.dates.endDate,
            hour: this.selectedHours,
            students: this.filterStudentsByHourRange(fromHour),
          },
        ];
      }
    },
    filterManagerConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filter.options.academicYears,
          value: this.filter.model.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'description',
          fieldKey: 'id',
          class: 'col-12 col-lg-2 mb-3 mb-lg-0',
          clearable: false,
          error: 'El año académico es requerido.',
          validations: 'required',
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filter.options.providers,
          value: this.filter.model.provider,
          disabled: this.filter.options.providers.length == 1,
          clearable: false,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-lg-3 mb-3 mb-lg-0',
          error: 'El proveedor es requerido.',
          validations: 'required',
        },
        {
          name: 'locationService',
          component: 'CustomDropDown',
          options: this.filter.options.locationServices,
          value: this.filter.model.locationService,
          disabled: this.filter.options.locationServices.length == 1,
          clearable: false,
          placeholder: 'Lugar de Servicio',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-lg-3 mb-3 mb-lg-0',
          error: 'El lugar de servicio es requerido.',
          validations: 'required',
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filter.options.disciplineTypes,
          value: this.filter.model.disciplineType,
          disabled: this.filter.options.disciplineTypes.length == 1,
          clearable: false,
          placeholder: 'Disciplina',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-lg-2 mb-3 mb-lg-0',
          error: 'La disciplina es requerida.',
          validations: 'required',
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filter.options.specialists,
          value: this.filter.model.specialist,
          clearable: false,
          disabled: this.filter.options.specialists.length == 1,
          placeholder: 'Especialista',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-lg-2 mb-3 mb-lg-0',
          error: 'El especialista es requerido.',
          validations: 'required',
        },
      ];
    },
    isProvider() {
      return [roles.Provider].includes(this.userRole);
    },
    isAssistantProvider() {
      return [roles.AsistenteProveedor].includes(this.userRole);
    },
    isSpecialist() {
      return [roles.Especialista].includes(this.userRole);
    },
  },
  async created() {
    this.hideNav();
    await this.getAcademicYears();
  },
  methods: {
    ...mapMutations(appModuleName, ['hideNav']),
    async onSelectHours(hours) {
      this.selectedHours = hours;
    },
    async onSearchHandler() {
      const isValid = await this.$refs.filter.$refs.formValidator.validate();
      if (!isValid) return;

      await this.search();
      await this.filterScheduleAvailability();
    },
    async search() {
      const { data } = await _getDetailBySpecialistAndDates({
        providerId: this.filter.model?.provider?.id,
        specialistId: this.filter.model?.specialist?.id,
        locationId: this.filter.model?.locationService?.id,
        disciplineTypeId: this.filter?.model?.disciplineType?.id,
        startDate: this.dates?.startDate,
        endDate: this.dates?.endDate,
      });
      this.availableStudents = data.map((item) => ({
        id: item.id,
        studentSieId: item.studentSieId,
        studentName: item.studentName,
        impedimentName: item.impedimentName,
        admissionId: item.admissionId,
        effectiveDateFrom: item.effectiveDateFrom,
        effectiveDateTo: item.effectiveDateTo,
        hasPlan: item.hasPlan,
        modalityId: item.modalityId,
        durationId: item.durationId,
        duration: item.minuteAmt,
        visits: item.visits.map((visit) => ({
          ...visit,
          hasChange: false,
          visitTimeStart: moment(
            `${visit.visitTimeStart.hours}:${visit.visitTimeStart.minutes}`,
            'h:mm'
          ).format('HH:mm'),
          visitTimeEnd: moment(
            `${visit.visitTimeEnd.hours}:${visit.visitTimeEnd.minutes}`,
            'h:mm'
          ).format('HH:mm'),
        })),
        schedules: item.schedules,
      }));
    },
    async onAcademicYearChange(value) {
      this.filter.model.provider = null;
      this.filter.model.locationService = null;
      this.filter.model.disciplineType = null;
      this.filter.model.specialist = null;

      this.filter.options = {
        ...this.filter.options,
        providers: [],
        locationServices: [],
        disciplineTypes: [],
        specialists: [],
      };

      if (!value) return;

      await this.getProviders();
    },
    async onProviderChange(value) {
      this.filter.model.locationService = null;
      this.filter.model.disciplineType = null;
      this.filter.model.specialist = null;

      this.filter.options = {
        ...this.filter.options,
        locationServices: [],
        disciplineTypes: [],
        specialists: [],
      };

      if (!value) return;

      await this.getLocations();
    },
    async onLocationServiceChange(value) {
      this.filter.model.disciplineType = null;
      this.filter.model.specialist = null;

      this.filter.options = {
        ...this.filter.options,
        disciplineTypes: [],
        specialists: [],
      };

      if (!value) return;

      await this.getDisciplineTypes();
    },
    async onDisciplineTypeChange(value) {
      this.filter.model.specialist = null;

      this.filter.options = {
        ...this.filter.options,
        specialists: [],
      };

      if (!value) return;

      await this.getSpecialist();
    },
    async getAcademicYears() {
      const academicYears = (await _getAcademicYearsToType('CURRENT', 26)).data;
      this.filter.options.academicYears = academicYears;
      this.filter.model.academicYear = this.currentAcademicYear();
      await this.getProviders();
    },
    async getProviders() {
      if (!this.isProvider && !this.isSpecialist) {
        const { data } = await getProvidersByAcademicyearId(
          this.filter.model.academicYear.id
        );
        this.filter.options.providers = data;
      } else {
        this.filter.options.providers = this.companies;
      }

      if (this.filter.options.providers.length == 1) {
        this.filter.model.provider = this.filter.options.providers[0];
        this.getLocations();
      }
    },
    async getLocations() {
      const { data } = await _getServiceLocations(
        this.filter.model.provider.id,
        null,
        null,
        this.filter.model.academicYear.id
      );
      this.filter.options.locationServices = data;

      if (this.filter.options.locationServices.length === 1) {
        this.filter.model.locationService = this.filter.options.locationServices[0];

        await this.getDisciplineTypes();
      }
    },
    async getDisciplineTypes() {
      const { data } = await _getDisciplineList(
        this.filter.model.provider.id,
        this.filter.model.locationService.id,
        serviceTypes.Therapy,
        this.filter.model.academicYear.id
      );
      this.filter.options.disciplineTypes = data;

      if (this.filter.options.disciplineTypes.length == 1) {
        this.filter.model.disciplineType = this.filter.options.disciplineTypes[0];

        await this.getSpecialist();
      }
    },
    async getSpecialist() {
      const { data } = await _getSpecialistList(
        this.filter.model.provider.id,
        this.filter.model.locationService.id,
        this.filter.model.disciplineType.id,
        this.filter.model.academicYear.id
      );
      this.filter.options.specialists = data;

      if (this.filter.options.specialists.length == 1) {
        this.filter.model.specialist = this.filter.options.specialists[0];
      }
    },
    onDatarangeChanged(rangeData) {
      this.dates = rangeData;
      if (this.filter.model.specialist) {
        this.search();
      }
    },
    filterStudentsByHourRange(fromHour) {
      const filteredObjects = this.availableStudents.filter((obj) => {
        if (obj.schedules && obj.schedules.length > 0) {
          return obj.schedules.some((schedule) => {
            const startHour = parseInt(moment(schedule.startDate).format('HH'));
            return startHour === fromHour;
          });
        }
        return false;
      });

      return filteredObjects;
    },
    async filterScheduleAvailability() {
      const { data } = await _getDetailBySpecialist({
        providerId: this.filter.model.provider.id,
        specialistId: this.filter.model.specialist.id,
        locationId: this.filter.model.locationService.id,
        disciplineTypeId: this.filter.model.disciplineType.id,
      });
      const uniqueHours = new Set();
      data.forEach((value) => {
        uniqueHours.add(value.timeStart.hours);
      });
      var hours = Array.from(uniqueHours).sort((a, b) => a - b);
      this.sortedHours = this.hourOptions.filter((option) => {
        return hours.includes(option.hourFrom);
      });
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep {
  .filter-manager_extended {
    border-radius: 0.25rem;
    @media only screen and (min-width: 750px) {
      .search-btn {
        &.col-lg-3 {
          flex: 0 0 10%;
          max-width: 10%;
          padding-left: 0;
        }
      }
    }
  }
}
</style>
